import i18n from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

import enAnalytics from "./locales/en/analytics.json";
import enApps from "./locales/en/apps.json";
import enAuth from "./locales/en/auth.json";
import enBruttovinst from "./locales/en/bruttovinst.json";
import enChat from "./locales/en/chat.json";
import enCommon from "./locales/en/common.json";
import enDashboard from "./locales/en/dashboard.json";
import enDocuments from "./locales/en/documents.json";
import enReports from "./locales/en/reports.json";
import enIntelligence from "./locales/en/intelligence.json";
import enLeads from "./locales/en/leads.json";
import enNav from "./locales/en/nav.json";
import enOnboarding from "./locales/en/onboarding.json";
import enProfile from "./locales/en/profile.json";
import enRecruit from "./locales/en/recruit.json";
import enSettings from "./locales/en/settings.json";
import enValidation from "./locales/en/validation.json";
import enAgera from "./locales/en/agera.json";
import enGoals from "./locales/en/goals.json";
import enMember from "./locales/en/member.json";
import enShell from "./locales/en/shell.json";
import enRoutines from "./locales/en/routines.json";
import enTeam from "./locales/en/team.json";
import enAnser from "./locales/en/anser.json";
import enUnderlag from "./locales/en/underlag.json";
import enHighFives from "./locales/en/high-fives.json";
import svAnalytics from "./locales/sv/analytics.json";
import svApps from "./locales/sv/apps.json";
import svAuth from "./locales/sv/auth.json";
import svBruttovinst from "./locales/sv/bruttovinst.json";
import svChat from "./locales/sv/chat.json";
import svCommon from "./locales/sv/common.json";
import svDashboard from "./locales/sv/dashboard.json";
import svDocuments from "./locales/sv/documents.json";
import svReports from "./locales/sv/reports.json";
import svIntelligence from "./locales/sv/intelligence.json";
import svLeads from "./locales/sv/leads.json";
import svNav from "./locales/sv/nav.json";
import svOnboarding from "./locales/sv/onboarding.json";
import svProfile from "./locales/sv/profile.json";
import svRecruit from "./locales/sv/recruit.json";
import svSettings from "./locales/sv/settings.json";
import svValidation from "./locales/sv/validation.json";
import svAgera from "./locales/sv/agera.json";
import svGoals from "./locales/sv/goals.json";
import svMember from "./locales/sv/member.json";
import svShell from "./locales/sv/shell.json";
import svRoutines from "./locales/sv/routines.json";
import svTeam from "./locales/sv/team.json";
import svAnser from "./locales/sv/anser.json";
import svUnderlag from "./locales/sv/underlag.json";
import svHighFives from "./locales/sv/high-fives.json";

/** Languages the app ships translations for. */
export const SUPPORTED_LANGUAGES = ["sv", "en"] as const;
export type AppLanguage = (typeof SUPPORTED_LANGUAGES)[number];

/** localStorage key for the user's chosen UI language (also used as the detector cache). */
export const GRAFTUS_LANGUAGE_KEY = "graftus_language";

/** Translation namespaces, one file per feature area. */
export const NAMESPACES = [
  "underlag",
  "anser",
  "highFives",
  "team",
  "routines",
  "shell",
  "member",
  "goals",
  "agera",
  "common",
  "dashboard",
  "nav",
  "onboarding",
  "auth",
  "apps",
  "recruit",
  "leads",
  "intelligence",
  "chat",
  "settings",
  "profile",
  "documents",
  "reports",
  "bruttovinst",
  "analytics",
  "validation",
] as const;

export const DEFAULT_NAMESPACE = "common";

export const resources = {
  sv: {
    underlag: svUnderlag,
    anser: svAnser,
    highFives: svHighFives,
    team: svTeam,
    routines: svRoutines,
    shell: svShell,
    member: svMember,
    goals: svGoals,
    agera: svAgera,
    common: svCommon,
    dashboard: svDashboard,
    nav: svNav,
    onboarding: svOnboarding,
    auth: svAuth,
    apps: svApps,
    recruit: svRecruit,
    leads: svLeads,
    intelligence: svIntelligence,
    chat: svChat,
    settings: svSettings,
    profile: svProfile,
    documents: svDocuments,
    reports: svReports,
    bruttovinst: svBruttovinst,
    analytics: svAnalytics,
    validation: svValidation,
  },
  en: {
    underlag: enUnderlag,
    anser: enAnser,
    highFives: enHighFives,
    team: enTeam,
    routines: enRoutines,
    shell: enShell,
    member: enMember,
    goals: enGoals,
    agera: enAgera,
    common: enCommon,
    dashboard: enDashboard,
    nav: enNav,
    onboarding: enOnboarding,
    auth: enAuth,
    apps: enApps,
    recruit: enRecruit,
    leads: enLeads,
    intelligence: enIntelligence,
    chat: enChat,
    settings: enSettings,
    profile: enProfile,
    documents: enDocuments,
    reports: enReports,
    bruttovinst: enBruttovinst,
    analytics: enAnalytics,
    validation: enValidation,
  },
} as const;

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "sv",
    supportedLngs: SUPPORTED_LANGUAGES as unknown as string[],
    // Map regional variants (e.g. en-US, en-GB) onto our base languages.
    load: "languageOnly",
    nonExplicitSupportedLngs: true,
    ns: NAMESPACES as unknown as string[],
    defaultNS: DEFAULT_NAMESPACE,
    detection: {
      // Prefer the user's saved choice; otherwise fall back to browser language.
      order: ["localStorage", "navigator"],
      lookupLocalStorage: GRAFTUS_LANGUAGE_KEY,
      caches: ["localStorage"],
    },
    interpolation: {
      // React already escapes values, so no double-escaping needed.
      escapeValue: false,
    },
  });

// Keep the document <html lang> in sync so the browser/AT know the page language.
i18n.on("languageChanged", (lng) => {
  if (typeof document !== "undefined") {
    document.documentElement.lang = lng;
  }
});

export default i18n;
